import { ApolloLink, HttpLink, InMemoryCacheConfig } from '@apollo/client'
import { onError } from '@apollo/client/link/error'
import Cookies from 'js-cookie'

import { API_HOST, STORAGE_LANG_KEY } from '@/constants'

import { CommonError, makeRequestWithRechallenge } from './auth'
import { localToken } from './token'

async function apolloFetcher(
  url: RequestInfo | URL,
  options: RequestInit = {}
) {
  return makeRequestWithRechallenge(async (newToken?: string) => {
    if (!options.headers) {
      options.headers = {}
    }

    if (newToken) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (options.headers as any)['Authorization']
      ;(options.headers as Record<string, string>)['Authorization'] =
        `Bearer ${newToken}`
    }

    // set language header
    if (typeof window !== 'undefined') {
      const lang = Cookies.get(STORAGE_LANG_KEY)
      ;(options.headers as Record<string, string>)['X-Accept-Language'] =
        lang || 'en'
    }

    const response = await fetch(url, options)

    if (response.status === 401) {
      const res = await response.clone().json()
      if ('code' in res && 'error' in res) {
        throw new CommonError(res)
      }
    }

    return response
  })
}

export type GraphQLResponseError = {
  name: string
  response: Response
  result: { code: number; error: string }
  statusCode: number
  message: string
}

export const httpLink = new HttpLink({
  uri: API_HOST + '/api/v2/graphql',
  fetch: apolloFetcher,
})

export const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers = {} as Record<string, string> }) => {
    if (
      typeof window !== 'undefined' &&
      !('Authorization' in headers) &&
      localToken
    ) {
      headers['Authorization'] = `Bearer ${localToken}`
    }

    return {
      headers: {
        ...headers,
        // 'Authorization': `Bearer ${token}`,
        // 'X-Accept-Language': getLanguage()
      },
    }
  })

  return forward(operation)
})

export const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) => {
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    })
  }
  if (networkError) {
    console.warn('[Network error]:', networkError.name, networkError.message)
  }
})

export const apolloCacheConfig: InMemoryCacheConfig = {
  typePolicies: {
    Query: {
      fields: {
        Command: {
          keyArgs: ['id'],
        },
        fetchCommands: {
          keyArgs: false,
          merge: (existing, incoming) => {
            if (!incoming) {
              return existing
            }

            const newEdges: { __ref: string }[] = [...(existing?.edges ?? [])]

            incoming.edges.forEach((incomingEdge: { __ref: string }) => {
              const existingIndex = newEdges.findIndex(
                (edge) => edge.__ref === incomingEdge.__ref
              )
              if (existingIndex > -1) {
                newEdges[existingIndex] = incomingEdge
              } else {
                newEdges.push(incomingEdge)
              }
            })

            const result = {
              ...incoming,
              edges: newEdges,
            }
            return result
          },
        },
      },
    },
  },
}
